import React, { useCallback, useEffect, useRef, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import plantumlEncoder from "plantuml-encoder";
import "./markdown-renderers.scss";

/**
 * Renderers shared by the published article and the editor preview, so what
 * the author sees while writing is what the post will actually look like.
 */

/**
 * Alt text derived from a pasted filename ("image", "image 2", "screenshot")
 * describes nothing, so it makes a poor caption even though it is still worth
 * keeping on the img for screen readers.
 */
function isDescriptiveAlt(alt?: string): boolean {
  if (!alt) return false;
  return !/^(image|screenshot|photo|pasted[\s-]?image|untitled)[\s-]*\d*$/i.test(
    alt.trim()
  );
}

export const BlogImage = (props: any) => {
  const [expanded, setExpanded] = useState(false);
  const thumbnailRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => {
    setExpanded(false);
    // Send focus back where it came from, rather than to the top of the page.
    thumbnailRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);

    // Stop the article scrolling behind the overlay.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded, close]);

  return (
    <>
      <button
        ref={thumbnailRef}
        type="button"
        className="blog-image-trigger"
        onClick={() => setExpanded(true)}
        aria-label={props.alt ? `Expand image: ${props.alt}` : "Expand image"}
      >
        <img
          className="blog-image"
          alt={props.alt}
          src={props.src}
          loading="lazy"
          decoding="async"
        />
      </button>

      {expanded ? (
        <div
          className="blog-image-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={props.alt || "Expanded image"}
          onClick={close}
        >
          <button
            ref={closeRef}
            type="button"
            className="blog-image-close"
            onClick={close}
            aria-label="Close image"
          >
            ×
          </button>
          <img
            className="blog-image-full"
            alt={props.alt}
            src={props.src}
            onClick={close}
          />
          {isDescriptiveAlt(props.alt) ? (
            <p className="blog-image-caption">{props.alt}</p>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export const PlantUMLBlock = ({ code }: { code: string }) => {
  const encoded = plantumlEncoder.encode(code);
  const url = `https://www.plantuml.com/plantuml/svg/${encoded}`;
  return <img src={url} alt="PlantUML diagram" loading="lazy" decoding="async" />;
};

/**
 * Authors do not always add an info string to a fenced block. Keep those
 * blocks legible, and recognize the common configuration formats used in the
 * infrastructure posts so they still receive useful token highlighting.
 */
function codeLanguage(className: string | undefined, code: string): string {
  const declaredLanguage = /language-([^\s]+)/.exec(className || "")?.[1];
  if (declaredLanguage) return declaredLanguage;

  const trimmed = code.trim();
  if (/^(---\s*\n)?\s*(?:\{|\[)/.test(trimmed)) return "json";
  if (
    /^(---\s*\n)?\s*[\w.-]+:\s*.+/m.test(trimmed) ||
    /^(apiVersion|kind|metadata|spec):/m.test(trimmed)
  ) {
    return "yaml";
  }
  if (/^#!|^\s*(kubectl|docker|npm|yarn|git|cd|export)\b/m.test(trimmed)) {
    return "bash";
  }
  if (/^\s*(?:async\s+)?def\s+\w+\s*\(|^\s*(?:from\s+\w+\s+)?import\s+\w+/m.test(trimmed)) {
    return "python";
  }
  return "text";
}

export const CodeBlock = ({ inline, className, children, ...props }: any) => {
  const [copied, setCopied] = useState(false);
  const codeString = String(children).replace(/\n$/, "");
  const language = codeLanguage(className, codeString);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  if (!inline) {
    if (language === "plantuml") {
      return <PlantUMLBlock code={codeString} />;
    }
    return (
      <div className="code-block-wrapper">
        <button
          type="button"
          className="copy-btn"
          onClick={handleCopy}
          aria-label={copied ? "Code copied" : "Copy code"}
          title={copied ? "Copied" : "Copy code"}
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <SyntaxHighlighter
          style={oneDark}
          language={language}
          PreTag="div"
          wrapLongLines={false}
          className="article-code-block"
          customStyle={{ margin: 0 }}
          {...props}
        >
          {codeString}
        </SyntaxHighlighter>
      </div>
    );
  }
  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

export const markdownRenderers = {
  img: BlogImage,
  code: CodeBlock,
};
